import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import {
  DOLLAR_TO_GOLD_CONVERSION as GOLD_RATE,
  DOLLAR_TO_SWEEP_CONVERSION as SC_RATE,
} from '@gains-com/shared';
import {
  CardComponent,
  CardContentComponent,
  IconComponent,
} from '@gains-com/ui-angular';
import { EnvironmentPipe } from '../../../../pipes/environment.pipe.js';
import { PurchaseDialogHeaderComponent } from '../purchase-dialog-header/purchase-dialog-header.component.js';
import { BoostVM } from '../../../../core/view-states/promotions-view.service.js';

const TIER_DISPLAY = [
  { label: 'VALUE', color: '#30D158', badgeBg: '#012909', badgeBorder: '#30D158' },
  { label: 'POPULAR', color: '#A479FB', badgeBg: '#100031', badgeBorder: '#A479FB' },
  { label: 'ELITE', color: '#0091FF', badgeBg: '#00274f', badgeBorder: '#0091FF' },
  { label: 'MAJOR', color: '#FF82FA', badgeBg: '#2c002a', badgeBorder: '#FF82FA' },
] as const;

export interface TierRow {
  price: number;
  bonusPercent: number;
  totalGCLabel: string;
  totalSCLabel: string;
  baseGCLabel: string;
  baseSCLabel: string;
  label: string;
  color: string;
  badgeBg: string;
  badgeBorder: string;
}

@Component({
  selector: 'app-first-time-boost-view',
  templateUrl: './first-time-boost-view.component.html',
  styleUrls: ['../styles.scss', './first-time-boost-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CardComponent,
    CardContentComponent,
    IconComponent,
    CurrencyPipe,
    PurchaseDialogHeaderComponent,
    EnvironmentPipe,
  ],
})
export class FirstTimeBoostViewComponent {
  boost = input.required<BoostVM>();

  readonly back = output<void>();
  readonly selectAmount = output<number>();
  readonly close = output<void>();

  readonly tierRows = computed<TierRow[]>(() => {
    const boost = this.boost();
    const tiers = boost.tiers ?? [];

    const sorted = [...tiers].sort((a, b) => Number(a.tier_price_usd) - Number(b.tier_price_usd));

    return sorted.map((tier, i) => {
      const price = Number(tier.tier_price_usd);
      const bonusPct = Number(tier.bonus_percentage);
      const baseGC = price * GOLD_RATE;
      const baseSC = price * SC_RATE;
      const totalGC = baseGC + baseGC * (bonusPct / 100);
      const totalSC = baseSC + baseSC * (bonusPct / 100);
      const meta = TIER_DISPLAY[i] ?? TIER_DISPLAY[0];

      return {
        price,
        bonusPercent: Math.round(bonusPct),
        totalGCLabel: this._formatAbbrev(totalGC) + ' GC',
        totalSCLabel: this._formatNumber(totalSC) + ' Free SC',
        baseGCLabel: this._formatAbbrev(baseGC) + ' GC',
        baseSCLabel: this._formatNumber(baseSC) + ' Free SC',
        label: meta.label,
        color: meta.color,
        badgeBg: meta.badgeBg,
        badgeBorder: meta.badgeBorder,
      };
    });
  });

  onTierClick(price: number) {
    this.selectAmount.emit(price);
  }

  private _formatAbbrev(value: number): string {
    if (value >= 1_000_000) {
      const m = value / 1_000_000;
      const label = m % 1 === 0 ? String(m) : m.toFixed(1).replace(/\.0$/, '');
      return label + 'M';
    }
    if (value >= 1_000) {
      const k = value / 1_000;
      const label = k % 1 === 0 ? String(k) : k.toFixed(1).replace(/\.0$/, '');
      return label + 'K';
    }
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(value);
  }

  private _formatNumber(value: number): string {
    return new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 }).format(Math.round(value));
  }
}
